import { useEffect } from "react";

import { useForm, useWatch } from "react-hook-form";

import { useCampaignLifeCycleChoiceContext } from "../Context/CampaignLifeCycleChoiceContext";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useLiveFormEditorExit } from "@/features/LiveFormEditor/Navigation/useLiveFormEditorExit";
import { useFormSaveAndNavigate } from "@/features/LiveFormEditor/Steps/useFormSaveAndNavigate";

export interface StepCampaignLifeCycleFormData {
  replacesTicketingId: string | null;
}

export const useCampaignLifeCycleChoice = () => {
  const { apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();
  const { setReplacesTicketingId } = useCampaignLifeCycleChoiceContext();
  const { exitLiveFormEditor } = useLiveFormEditorExit();
  const utils = trpc.useUtils();
  const { logAmplitudeEvent } = useAmplitude();

  // Session replay targets sessions that open this step, so the view is logged once when the step mounts
  useEffect(() => {
    logAmplitudeEvent(AmplitudeEvents.MembershipCampaignLifeCycleStepViewed, { form_id: apiTicketing.id });
  }, [apiTicketing.id, logAmplitudeEvent]);

  const { data: replaceableCampaigns } = trpc.getReplaceableMembershipCampaigns.useQuery({
    ticketingId: apiTicketing.id,
  });

  const form = useForm<StepCampaignLifeCycleFormData>({
    values: { replacesTicketingId: replaceableCampaigns?.replacesTicketingId ?? null },
  });
  const replacesTicketingId = useWatch({ control: form.control, name: "replacesTicketingId" });

  // The preview illustration is rendered outside the step, so it follows the choice through a shared context
  useEffect(() => {
    setReplacesTicketingId(replacesTicketingId);
  }, [replacesTicketingId, setReplacesTicketingId]);

  const selectChoice = (choice: string | null) => {
    form.setValue("replacesTicketingId", choice, { shouldDirty: true });
  };

  const { mutateAsync: setMembershipCampaignReplacement } = trpc.setMembershipCampaignReplacement.useMutation();
  const saveData = async (formData: StepCampaignLifeCycleFormData) => {
    const { error } = await setMembershipCampaignReplacement({
      ticketingId: apiTicketing.id,
      replacesTicketingId: formData.replacesTicketingId,
    });
    if (error) {
      return { data: null, error };
    }
    await utils.getReplaceableMembershipCampaigns.invalidate({ ticketingId: apiTicketing.id });
    const ticketing = await utils.getTicketing.fetch({ ticketingId: apiTicketing.id });
    return { data: ticketing ?? null, error: null };
  };

  const { handleSaveAndNext } = useFormSaveAndNavigate<StepCampaignLifeCycleFormData>({
    type: "ticketing",
    form,
    saveData,
  });

  return {
    form,
    campaigns: replaceableCampaigns?.campaigns ?? [],
    replacesTicketingId,
    selectChoice,
    handleSaveAndNext,
    handleExit: exitLiveFormEditor,
  };
};
